import * as React from 'react';

import { t } from '~/i18n';
import { formatDateTime } from '@ixicare/ui';
import { observer } from 'mobx-react';
import Alert from '~store/Alert/Alert';
import { HcpGroupHistory } from '@ixicare/resources';
import { useStores } from '~store/utils';
import { useHcpGroups } from '~hooks/useHcpGroups';

interface AlertSegmentationLogsProps {
  alert: Alert;
}
const AlertSegmentationLogs: React.FC<AlertSegmentationLogsProps> = observer(({ alert }) => {
  const { alerts } = useStores();

  React.useEffect(() => {
    alerts.fetchAlert(alert?.uuid);
  }, [alert?.uuid]);

  return (
    <ul className="list">
      {alert.hcpGroupHistory
        ?.slice()
        .sort((a, b) => {
          return a.counter - b.counter;
        })
        .map((historyEntry, index) => (
          <HcpGroupHistoryItem
            key={`${historyEntry.counter}-${index}`}
            historyEntry={historyEntry}
            siteUuid={alert.siteUuid ?? undefined}
          />
        ))}
    </ul>
  );
});

export function HcpGroupHistoryItem({ historyEntry, siteUuid }: { historyEntry: HcpGroupHistory; siteUuid?: string }) {
  const { data: hcpGroups } = useHcpGroups(siteUuid, { staleTime: 5 * 60 * 1000 });
  return <li>{hcpGroupHistoryText(historyEntry, hcpGroups)}</li>;
}

/** The log line for a segmentation or escalation step, as shown in the segmentation logs. */
export function hcpGroupHistoryText(historyEntry: HcpGroupHistory, hcpGroups?: { id: string; hcpGroupName: string }[]) {
  if (historyEntry.noSegmentation) {
    return t('alert.segmentation.logs.no_segmentation', {
      datetime: formatDateTime(historyEntry.createdAt),
      reason: historyEntry.reason || '',
    });
  }

  const groupName = historyEntry.hcpGroupId
    ? (hcpGroups?.find((g) => g.id === historyEntry.hcpGroupId)?.hcpGroupName ?? historyEntry.hcpGroupId)
    : null;
  const translationKey = historyEntry.reason
    ? 'alert.segmentation.logs.group_with_reason'
    : 'alert.segmentation.logs.group';

  return t(translationKey, {
    datetime: formatDateTime(historyEntry.createdAt),
    groupName: groupName || '-',
    reason: historyEntry.reason || '',
  });
}

export default AlertSegmentationLogs;
